<script setup lang="ts">
import { computed, ref } from "vue";
import type { DRLocalDrawingMeta } from "@/types";

import { useDrawingStore } from "@/stores/fileStore";
import SyncDrawingItem from "@/components/files/SyncList/SyncDrawingItem.vue";
import { isDrawingSynced } from "@/utils/drawings.utils";

const drawingStore = useDrawingStore();

const showErrorModal = ref(false);
const showDraftModal = ref(false);

const localData = computed<{
  errorDrawings: DRLocalDrawingMeta[];
  unsyncedDrawings: DRLocalDrawingMeta[];
}>(() => {
  const drawings: DRLocalDrawingMeta[] = drawingStore.localDrawings ?? [];
  return drawings.reduce(
    (acc, drawing) => {
      if (drawing.error) {
        acc.errorDrawings.push(drawing);
      } else if (!isDrawingSynced(drawing)) {
        acc.unsyncedDrawings.push(drawing);
      }
      return acc;
    },
    {
      errorDrawings: [] as DRLocalDrawingMeta[],
      unsyncedDrawings: [] as DRLocalDrawingMeta[],
    }
  );
});
</script>

<template>
  <div class="sync-page">
    <header class="mb-5">
      <h1 class="h3 mb-2">
        <i class="bi bi-cloud-slash me-2"></i>
        {{ $t("dashboard:sync.page.title") }}
      </h1>
      <p class="text-muted">{{ $t("dashboard:sync.page.subtitle") }}</p>
    </header>

    <div
      v-if="
        !localData.errorDrawings.length && !localData.unsyncedDrawings.length
      "
      class="my-5"
    >
      <el-empty :description="$t('dashboard:sync.page.empty')" />
    </div>

    <!-- Erreurs de synchronisation -->

    <section
      v-if="localData.errorDrawings.length"
      class="bordered bg-white rounded p-3"
    >
      <div class="d-flex align-items-start gap-3">
        <div>
          <div
            class="rounded bg-danger-light square d-inline-flex justify-content-center align-items-center"
          >
            <el-icon size="1.3rem">
              <i class="text-danger bi-exclamation-circle-fill"></i
            ></el-icon>
          </div>
        </div>
        <div class="col">
          <h2 class="h5 mb-1">
            {{ $t("dashboard:sync.page.errorSection.title") }}
          </h2>
          <p class="section-subtitle">
            {{ $t("dashboard:sync.page.errorSection.subtitle") }}
          </p>
        </div>
        <button class="btn btn-link p-0 ms-2" @click="showErrorModal = true">
          <i class="bi bi-info-circle"></i>
        </button>
      </div>

      <el-divider class="mt-0 mb-2" />

      <ul class="list-group w-100">
        <SyncDrawingItem
          v-for="drawing in localData.errorDrawings"
          :key="`error-${drawing.id}`"
          :localData="drawing"
        />
      </ul>
    </section>

    <!-- Brouillons locaux -->
    <section
      v-if="localData.unsyncedDrawings.length"
      class="bordered bg-white rounded p-3"
    >
      <div class="d-flex align-items-start gap-3">
        <div>
          <div
            class="rounded bg-warning-light square d-inline-flex justify-content-center align-items-center"
          >
            <el-icon size="1.3rem">
              <i class="text-warning bi-pencil-fill"></i
            ></el-icon>
          </div>
        </div>
        <div class="col">
          <h2 class="h5 mb-1">
            {{ $t("dashboard:sync.page.draftSection.title") }}
          </h2>
          <p class="section-subtitle">
            {{ $t("dashboard:sync.page.draftSection.subtitle") }}
          </p>
        </div>
        <button class="btn btn-link p-0 ms-2" @click="showDraftModal = true">
          <i class="bi bi-info-circle"></i>
        </button>
      </div>

      <el-divider class="mt-0 mb-2" />

      <ul class="list-group w-100">
        <SyncDrawingItem
          v-for="drawing in localData.unsyncedDrawings"
          :key="`unsynced-${drawing.id}`"
          :localData="drawing"
        />
      </ul>
    </section>

    <!-- Modal erreurs -->
    <el-dialog
      v-model="showErrorModal"
      :title="$t('dashboard:sync.page.errorSection.title')"
      width="400"
    >
      <p>{{ $t("dashboard:sync.page.errorSection.description") }}</p>
    </el-dialog>

    <!-- Modal brouillons -->
    <el-dialog
      v-model="showDraftModal"
      :title="$t('dashboard:sync.page.draftSection.title')"
      width="400"
    >
      <p>{{ $t("dashboard:sync.page.draftSection.description") }}</p>
    </el-dialog>
  </div>
</template>

<style scoped>
.sync-page {
  max-width: 500px;
  margin: 2rem auto;
  padding: 0 1rem;
}

.section-subtitle {
  font-size: 0.8rem;
  opacity: 0.7;
  margin-bottom: 1rem;
}

.square {
  width: 2.8rem;
  height: 2.8rem;
}
</style>
